import { Checkbox, Radio, RadioGroup, Text, blue, neutral } from "@flat-ui";
import { Box } from "@mui/material";
import {
  feedbackControlWrapperSx,
  feedbackOptionRowSx,
} from "@components/analytics/in-house/feedback/analytics-feedback-helper";
import { SurveyOption } from "@components/analytics/in-house/feedback/analytics-feedback-types";

interface Props {
  /** Options to render as selectable rows. */
  options: SurveyOption[];

  /** Currently selected option values. */
  selectedValues: string[];

  /** When true, multiple options can be selected (checkboxes); otherwise a single option (radios). */
  isMultiSelect: boolean;

  /** Called with the option value whenever a row is toggled. */
  onToggle: (value: string) => void;
}

/** Renders a vertical list of selectable option rows for a survey step. */
export function AnalyticsFeedbackOptionList({
  options,
  selectedValues,
  isMultiSelect,
  onToggle,
}: Props): JSX.Element {
  const selectedValue = selectedValues[0] ?? "";

  const rows = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {options.map((option) => {
        const isSelected = selectedValues.includes(option.value);

        return (
          <Box
            key={option.value}
            role="button"
            onClick={() => onToggle(option.value)}
            sx={{
              ...feedbackOptionRowSx,
              borderColor: isSelected ? blue[200] : neutral[200],
              backgroundColor: isSelected ? blue[50] : "transparent",
            }}
          >
            <Box sx={feedbackControlWrapperSx}>
              {isMultiSelect ? (
                <Checkbox checked={isSelected} readOnly controlSx={{ m: 0 }} />
              ) : (
                <Radio value={option.value} checked={isSelected} />
              )}
            </Box>
            <Text variant="bodyM">{option.label}</Text>
          </Box>
        );
      })}
    </Box>
  );

  if (isMultiSelect) {
    return rows;
  }

  return (
    <RadioGroup value={selectedValue} onChange={(_, value) => onToggle(value)}>
      {rows}
    </RadioGroup>
  );
}
